import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Users, Clock, Plus, Edit2, Trash2, Eye, FileText, MessageSquare } from 'lucide-react';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJobModal, setShowJobModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [showApplicationsModal, setShowApplicationsModal] = useState(false);
  const [selectedJobApps, setSelectedJobApps] = useState([]);

  const [jobForm, setJobForm] = useState({
    title: '',
    description: '',
    location: '',
    salary_range: '',
    job_type: 'Full-time',
    requirements: '',
    responsibilities: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    await Promise.all([
      fetchStats(),
      fetchJobs(),
      fetchApplications(),
      fetchMessages()
    ]);
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const data = await api.admin.getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchJobs = async () => {
    try {
      const data = await api.jobs.getAll();
      setJobs(data);
    } catch (error) {
      toast.error('Failed to load jobs');
    }
  };

  const fetchApplications = async () => {
    try {
      const data = await api.applications.getAll();
      setApplications(data);
    } catch (error) {
      console.error('Failed to load applications');
    }
  };

  const fetchMessages = async () => {
    try {
      const data = await api.admin.getMessages();
      setMessages(data);
    } catch (error) {
      console.error('Failed to load messages');
    }
  };

  const handleCreateJob = () => {
    setEditingJob(null);
    setJobForm({
      title: '',
      description: '',
      location: '',
      salary_range: '',
      job_type: 'Full-time',
      requirements: '',
      responsibilities: ''
    });
    setShowJobModal(true);
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setJobForm({
      title: job.title,
      description: job.description,
      location: job.location,
      salary_range: job.salary_range,
      job_type: job.job_type,
      requirements: job.requirements.join('\n'),
      responsibilities: job.responsibilities.join('\n')
    });
    setShowJobModal(true);
  };

  const handleSaveJob = async () => {
    const payload = {
      ...jobForm,
      requirements: jobForm.requirements.split('\n').filter(r => r.trim()),
      responsibilities: jobForm.responsibilities.split('\n').filter(r => r.trim())
    };

    try {
      if (editingJob) {
        await api.jobs.update(editingJob.id, payload);
        toast.success('Job updated!');
      } else {
        await api.jobs.create(payload);
        toast.success('Job created!');
      }
      setShowJobModal(false);
      fetchJobs();
      fetchStats();
    } catch (error) {
      toast.error('Failed to save job');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;

    try {
      await api.jobs.delete(jobId);
      toast.success('Job deleted');
      fetchJobs();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const handleViewApplications = async (jobId) => {
    try {
      const data = await api.applications.getByJob(jobId);
      setSelectedJobApps(data);
      setShowApplicationsModal(true);
    } catch (error) {
      toast.error('Failed to load applications');
    }
  };

  const handleUpdateStatus = async (appId, newStatus) => {
    try {
      await api.applications.updateStatus(appId, newStatus);
      toast.success('Status updated');
      fetchApplications();
      if (showApplicationsModal) {
        const updatedApps = selectedJobApps.map(app =>
          app.id === appId ? { ...app, status: newStatus } : app
        );
        setSelectedJobApps(updatedApps);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleToggleRead = async (msgId, currentStatus) => {
    try {
      await api.admin.updateMessageReadStatus(msgId, !currentStatus);
      toast.success(`Message marked as ${!currentStatus ? 'read' : 'unread'}`);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to update message status');
    }
  };

  const handleDeleteMessage = async (msgId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await api.admin.deleteMessage(msgId);
      toast.success('Message deleted');
      fetchMessages();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />

      <div className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h1 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="admin-dashboard-heading">
              Admin <span className="gradient-text">Dashboard</span>
            </h1>
            <p className="text-lg text-slate-400">Manage jobs, applications, and users</p>
          </div>

          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12" data-testid="admin-stats">
              <div className="glass-card rounded-2xl p-6">
                <Briefcase className="w-8 h-8 text-cyan-500 mb-4" />
                <div className="text-3xl font-bold mb-2">{stats.total_jobs}</div>
                <div className="text-sm text-slate-400">Active Jobs</div>
              </div>
              <div className="glass-card rounded-2xl p-6">
                <FileText className="w-8 h-8 text-cyan-500 mb-4" />
                <div className="text-3xl font-bold mb-2">{stats.total_applications}</div>
                <div className="text-sm text-slate-400">Applications</div>
              </div>
              <div className="glass-card rounded-2xl p-6">
                <Clock className="w-8 h-8 text-yellow-500 mb-4" />
                <div className="text-3xl font-bold mb-2">{stats.pending_applications}</div>
                <div className="text-sm text-slate-400">Pending</div>
              </div>
              <div className="glass-card rounded-2xl p-6">
                <Users className="w-8 h-8 text-cyan-500 mb-4" />
                <div className="text-3xl font-bold mb-2">{stats.total_users}</div>
                <div className="text-sm text-slate-400">Users</div>
              </div>
            </div>
          )}

          <Tabs defaultValue="jobs" className="space-y-8">
            <TabsList className="bg-slate-900 border border-slate-800">
              <TabsTrigger value="jobs" data-testid="jobs-tab">Jobs Management</TabsTrigger>
              <TabsTrigger value="applications" data-testid="applications-tab">Applications</TabsTrigger>
              <TabsTrigger value="messages" data-testid="messages-tab">Messages</TabsTrigger>
            </TabsList>

            <TabsContent value="jobs">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold">Manage Jobs</h2>
                <button
                  onClick={handleCreateJob}
                  className="btn-primary flex items-center gap-2"
                  data-testid="create-job-btn"
                >
                  <Plus className="w-5 h-5" />
                  Create Job
                </button>
              </div>

              <div className="grid gap-6" data-testid="jobs-list">
                {jobs.map((job) => (
                  <div key={job.id} className="glass-card rounded-2xl p-6" data-testid={`job-card-${job.id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-semibold mb-2">{job.title}</h3>
                        <p className="text-slate-400 text-sm">{job.location} • {job.job_type}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewApplications(job.id)}
                          className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
                          data-testid={`view-apps-btn-${job.id}`}
                        >
                          <Eye className="w-5 h-5 text-cyan-500" />
                        </button>
                        <button
                          onClick={() => handleEditJob(job)}
                          className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
                          data-testid={`edit-job-btn-${job.id}`}
                        >
                          <Edit2 className="w-5 h-5 text-blue-500" />
                        </button>
                        <button
                          onClick={() => handleDeleteJob(job.id)}
                          className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
                          data-testid={`delete-job-btn-${job.id}`}
                        >
                          <Trash2 className="w-5 h-5 text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="applications">
              <h2 className="text-2xl font-semibold mb-6">All Applications</h2>

              <div className="grid gap-6" data-testid="applications-list">
                {applications.map((app) => (
                  <div key={app.id} className="glass-card rounded-2xl p-6" data-testid={`application-${app.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold mb-2">{app.user_name}</h3>
                        <p className="text-slate-400 text-sm mb-1">{app.user_email}</p>
                        <p className="text-slate-500 text-sm">Job ID: {app.job_id.slice(0, 8)}</p>
                        {app.resume_path && (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${app.resume_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-cyan-500 hover:text-cyan-400 mt-2 hover:underline text-sm"
                          >
                            <FileText className="w-4 h-4" />
                            View Resume
                          </a>
                        )}
                      </div>
                      <select
                        value={app.status}
                        onChange={(e) => handleUpdateStatus(app.id, e.target.value)}
                        className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm"
                        data-testid={`status-select-${app.id}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="reviewed">Reviewed</option>
                        <option value="accepted">Accepted</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="messages">
              <h2 className="text-2xl font-semibold mb-6">Contact Messages</h2>
              <div className="grid gap-6">
                {messages.length === 0 ? (
                  <p className="text-slate-400 text-center py-8">No messages yet</p>
                ) : (
                  messages.map((msg) => (
                    <div key={msg.id} className={`glass-card rounded-2xl p-6 transition-all ${msg.is_read ? 'opacity-60' : 'border-l-4 border-l-cyan-500'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-lg font-semibold">{msg.subject}</h3>
                            {!msg.is_read && (
                              <span className="px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-500 text-[10px] font-bold uppercase tracking-wider">New</span>
                            )}
                          </div>
                          <p className="text-slate-400 text-sm">{msg.name} ({msg.email})</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="text-xs text-slate-500">{new Date(msg.created_at).toLocaleString()}</span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleToggleRead(msg.id, msg.is_read)}
                              className={`p-1.5 rounded-lg transition-colors ${msg.is_read ? 'hover:bg-slate-800 text-slate-500' : 'hover:bg-cyan-500/10 text-cyan-500'}`}
                              title={msg.is_read ? "Mark as unread" : "Mark as read"}
                            >
                              <MessageSquare className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteMessage(msg.id)}
                              className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-500 transition-colors"
                              title="Delete message"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <p className="text-slate-300 mt-4 whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Dialog open={showJobModal} onOpenChange={setShowJobModal}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="job-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingJob ? 'Edit Job' : 'Create New Job'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2">Job Title *</label>
              <Input
                value={jobForm.title}
                onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
                className="bg-slate-800 border-slate-700"
                data-testid="job-title-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Description *</label>
              <Textarea
                value={jobForm.description}
                onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                className="min-h-24 bg-slate-800 border-slate-700"
                data-testid="job-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Location *</label>
                <Input
                  value={jobForm.location}
                  onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  data-testid="job-location-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Salary Range *</label>
                <Input
                  value={jobForm.salary_range}
                  onChange={(e) => setJobForm({ ...jobForm, salary_range: e.target.value })}
                  placeholder="e.g., ₹80k - ₹120k"
                  className="bg-slate-800 border-slate-700"
                  data-testid="job-salary-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Job Type *</label>
              <select
                value={jobForm.job_type}
                onChange={(e) => setJobForm({ ...jobForm, job_type: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700"
                data-testid="job-type-select"
              >
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Contract">Contract</option>
                <option value="Remote">Remote</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Requirements (one per line) *</label>
              <Textarea
                value={jobForm.requirements}
                onChange={(e) => setJobForm({ ...jobForm, requirements: e.target.value })}
                className="min-h-24 bg-slate-800 border-slate-700"
                placeholder="Bachelor's degree in CS&#10;5+ years experience&#10;..."
                data-testid="job-requirements-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Responsibilities (one per line) *</label>
              <Textarea
                value={jobForm.responsibilities}
                onChange={(e) => setJobForm({ ...jobForm, responsibilities: e.target.value })}
                className="min-h-24 bg-slate-800 border-slate-700"
                placeholder="Lead development team&#10;Design architecture&#10;..."
                data-testid="job-responsibilities-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSaveJob}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                data-testid="save-job-btn"
              >
                {editingJob ? 'Update Job' : 'Create Job'}
              </Button>
              <Button
                onClick={() => setShowJobModal(false)}
                variant="outline"
                className="border-slate-700 hover:bg-slate-800"
                data-testid="cancel-job-btn"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showApplicationsModal} onOpenChange={setShowApplicationsModal}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="applications-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Job Applications</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedJobApps.length === 0 ? (
              <p className="text-slate-400 text-center py-8">No applications yet</p>
            ) : (
              selectedJobApps.map((app) => (
                <div key={app.id} className="p-4 rounded-lg bg-slate-800 border border-slate-700">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-semibold">{app.user_name}</h4>
                      <p className="text-sm text-slate-400">{app.user_email}</p>
                    </div>
                    <select
                      value={app.status}
                      onChange={(e) => handleUpdateStatus(app.id, e.target.value)}
                      className="px-3 py-1 rounded-lg bg-slate-900 border border-slate-600 text-sm"
                    >
                      <option value="pending">Pending</option>
                      <option value="reviewed">Reviewed</option>
                      <option value="accepted">Accepted</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div className="text-sm text-slate-300">
                    <strong>Cover Letter:</strong>
                    <p className="mt-1 text-slate-400">{app.cover_letter}</p>
                    {app.resume_path && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${app.resume_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-cyan-500 hover:text-cyan-400 mt-3 hover:underline"
                      >
                        <FileText className="w-4 h-4" />
                        View Resume
                      </a>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
